const DADATA_ADDRESS_SUGGEST_URL = 'https://suggestions.dadata.ru/suggestions/api/4_1/rs/suggest/address';

export type CitySuggestionOption = {
  label: string;
  geoId?: string;
  meta?: string;
  timezone?: string;
};

export type AddressSuggestionOption = {
  label: string;
  geoId?: string;
  meta?: string;
  cityLabel?: string;
  timezone?: string;
};

const KLADR_REGION_TIMEZONE: Record<string, string> = {
  '01': 'Europe/Moscow',
  '02': 'Asia/Yekaterinburg',
  '03': 'Asia/Irkutsk',
  '04': 'Asia/Barnaul',
  '05': 'Europe/Moscow',
  '06': 'Europe/Moscow',
  '07': 'Europe/Moscow',
  '08': 'Europe/Moscow',
  '09': 'Europe/Moscow',
  '10': 'Europe/Moscow',
  '11': 'Europe/Moscow',
  '12': 'Europe/Moscow',
  '13': 'Europe/Moscow',
  '14': 'Asia/Yakutsk',
  '15': 'Europe/Moscow',
  '16': 'Europe/Moscow',
  '17': 'Asia/Krasnoyarsk',
  '18': 'Europe/Samara',
  '19': 'Asia/Krasnoyarsk',
  '20': 'Europe/Moscow',
  '21': 'Europe/Moscow',
  '22': 'Asia/Barnaul',
  '23': 'Europe/Moscow',
  '24': 'Asia/Krasnoyarsk',
  '25': 'Asia/Vladivostok',
  '26': 'Europe/Moscow',
  '27': 'Asia/Vladivostok',
  '28': 'Asia/Yakutsk',
  '29': 'Europe/Moscow',
  '30': 'Europe/Astrakhan',
  '31': 'Europe/Moscow',
  '32': 'Europe/Moscow',
  '33': 'Europe/Moscow',
  '34': 'Europe/Volgograd',
  '35': 'Europe/Moscow',
  '36': 'Europe/Moscow',
  '37': 'Europe/Moscow',
  '38': 'Asia/Irkutsk',
  '39': 'Europe/Kaliningrad',
  '40': 'Europe/Moscow',
  '41': 'Asia/Kamchatka',
  '42': 'Asia/Krasnoyarsk',
  '43': 'Europe/Kirov',
  '44': 'Europe/Moscow',
  '45': 'Asia/Yekaterinburg',
  '46': 'Europe/Moscow',
  '47': 'Europe/Moscow',
  '48': 'Europe/Moscow',
  '49': 'Asia/Magadan',
  '50': 'Europe/Moscow',
  '51': 'Europe/Moscow',
  '52': 'Europe/Moscow',
  '53': 'Europe/Moscow',
  '54': 'Asia/Novosibirsk',
  '55': 'Asia/Omsk',
  '56': 'Asia/Yekaterinburg',
  '57': 'Europe/Moscow',
  '58': 'Europe/Moscow',
  '59': 'Asia/Yekaterinburg',
  '60': 'Europe/Moscow',
  '61': 'Europe/Moscow',
  '62': 'Europe/Moscow',
  '63': 'Europe/Samara',
  '64': 'Europe/Saratov',
  '65': 'Asia/Sakhalin',
  '66': 'Asia/Yekaterinburg',
  '67': 'Europe/Moscow',
  '68': 'Europe/Moscow',
  '69': 'Europe/Moscow',
  '70': 'Asia/Tomsk',
  '71': 'Europe/Moscow',
  '72': 'Asia/Yekaterinburg',
  '73': 'Europe/Ulyanovsk',
  '74': 'Asia/Yekaterinburg',
  '75': 'Asia/Chita',
  '76': 'Europe/Moscow',
  '77': 'Europe/Moscow',
  '78': 'Europe/Moscow',
  '79': 'Asia/Vladivostok',
  '83': 'Europe/Moscow',
  '86': 'Asia/Yekaterinburg',
  '87': 'Asia/Anadyr',
  '89': 'Asia/Yekaterinburg',
  '91': 'Europe/Moscow',
  '92': 'Europe/Moscow',
  '95': 'Europe/Moscow',
  '99': 'Europe/Moscow',
};

type DadataSuggestionItem = {
  value: string;
  data?: {
    city?: string | null;
    settlement?: string | null;
    timezone?: string | null;
    timezone_ext?: { name?: string | null } | null;
    kladr_id?: string | null;
  };
};

type DadataAddressResponse = {
  suggestions?: DadataSuggestionItem[];
};

function resolveTimezone(data?: DadataSuggestionItem['data']): string | undefined {
  const direct = data?.timezone?.trim();
  if (direct) return direct;

  const extended = data?.timezone_ext?.name?.trim();
  if (extended) return extended;

  const regionCode = data?.kladr_id?.slice(0, 2);
  if (regionCode) return KLADR_REGION_TIMEZONE[regionCode];

  return undefined;
}

function buildCityGeoId(data?: DadataSuggestionItem['data']): string | undefined {
  const kladr = data?.kladr_id?.trim();
  if (kladr) return `fias:${kladr}`;
  return undefined;
}

export async function fetchCitySuggestions(query: string, token: string): Promise<CitySuggestionOption[]> {
  const response = await fetch(DADATA_ADDRESS_SUGGEST_URL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      Authorization: `Token ${token}`,
    },
    body: JSON.stringify({
      query,
      count: 10,
      from_bound: { value: 'city' },
      to_bound: { value: 'settlement' },
    }),
  });

  if (!response.ok) {
    throw new Error(`DaData request failed: ${response.status}`);
  }

  const payload = (await response.json()) as DadataAddressResponse;
  const options = new Map<string, CitySuggestionOption>();
  for (const item of payload.suggestions ?? []) {
    const label = (item.data?.city || item.data?.settlement || item.value || '').trim();
    if (!label) continue;
    const timezone = resolveTimezone(item.data);
    const geoId = buildCityGeoId(item.data);
    const meta = JSON.stringify(item.data ?? {});

    // Prefer a version that contains timezone.
    const existing = options.get(label);
    if (!existing || (!existing.timezone && timezone)) {
      options.set(label, { label, timezone, geoId, meta });
    }
  }

  return Array.from(options.values());
}

export async function fetchAddressSuggestions(query: string, token: string): Promise<AddressSuggestionOption[]> {
  const response = await fetch(DADATA_ADDRESS_SUGGEST_URL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      Authorization: `Token ${token}`,
    },
    body: JSON.stringify({
      query,
      count: 10,
    }),
  });

  if (!response.ok) {
    throw new Error(`DaData request failed: ${response.status}`);
  }

  const payload = (await response.json()) as DadataAddressResponse;
  const options = new Map<string, AddressSuggestionOption>();
  for (const item of payload.suggestions ?? []) {
    const label = (item.value || '').trim();
    if (!label) continue;
    const timezone = resolveTimezone(item.data);
    const geoId = buildCityGeoId(item.data);
    const cityLabel = (item.data?.city || item.data?.settlement || '').trim() || undefined;
    const meta = JSON.stringify(item.data ?? {});

    const existing = options.get(label);
    if (!existing || (!existing.timezone && timezone)) {
      options.set(label, { label, timezone, geoId, meta, cityLabel });
    }
  }

  return Array.from(options.values());
}
